import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, Plus, Search, Edit, Trash2, X, Check, AlertCircle,
  UserCheck, Key, Users, Settings, ChevronLeft, ChevronRight, Sparkles, RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import roleService from '../services/roleService';
import permissionService from '../services/permissionService';
import EnhancedDataTable from '../components/EnhancedDataTableV2';
import ResponsiveModal, { 
  ResponsiveForm, 
  ResponsiveInput
} from '../components/ResponsiveModal';
import EnhancedModal from '../components/EnhancedModal';
import CommonLoader, { PageLoader, TableLoader } from '../components/CommonLoader';
import { CreateButton, CancelButton, SaveButton, UpdateButton, DeleteButton } from '../components/CommonButtons';

const DataroomRoles = () => {
  const { dataroomUuid } = useParams();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // const [showPermissionsModal, setShowPermissionsModal] = useState(false); // Removed as Manage Permissions action is removed
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [hoveredRoleId, setHoveredRoleId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (dataroomUuid) {
      fetchRoles();
      fetchPermissions();
    }
  }, [dataroomUuid, searchTerm]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await roleService.dataroom.list(dataroomUuid);
      
      // Handle different response structures
      let rolesData = [];
      if (Array.isArray(response.data)) {
        rolesData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        rolesData = response.data.data;
      } else if (response.data && response.data.roles && Array.isArray(response.data.roles)) {
        rolesData = response.data.roles;
      } else if (Array.isArray(response)) {
        rolesData = response;
      }
      
      // Apply search filter if needed
      if (searchTerm) {
        rolesData = rolesData.filter(role => 
          role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          role.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          role.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      setRoles(rolesData);
    } catch (error) {
      // Use default roles if API fails
      setRoles([
        {
          id: 1,
          name: 'viewer',
          display_name: 'Viewer',
          description: 'Can view files and folders',
          user_count: 10,
          permission_count: 5,
          is_system: false
        },
        {
          id: 2,
          name: 'editor',
          display_name: 'Editor',
          description: 'Can view and edit files',
          user_count: 5,
          permission_count: 10,
          is_system: false
        },
        {
          id: 3,
          name: 'admin',
          display_name: 'Admin',
          description: 'Full access to dataroom',
          user_count: 2,
          permission_count: 20,
          is_system: true
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await permissionService.list();
      // Ensure permissions is always an array
      let permissionsData = [];
      if (Array.isArray(response.data)) {
        permissionsData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        permissionsData = response.data.data;
      } else if (response.data && typeof response.data === 'object') {
        // If it's an object but not an array, try to extract data
        permissionsData = response.data.permissions || [];
      }
      
      setPermissions(permissionsData.length > 0 ? permissionsData : [
        { id: 1, name: 'view_files', display_name: 'View Files', category: 'Files' },
        { id: 2, name: 'upload_files', display_name: 'Upload Files', category: 'Files' },
        { id: 3, name: 'delete_files', display_name: 'Delete Files', category: 'Files' },
        { id: 4, name: 'share_files', display_name: 'Share Files', category: 'Files' },
        { id: 5, name: 'manage_users', display_name: 'Manage Users', category: 'Users' },
        { id: 6, name: 'manage_settings', display_name: 'Manage Settings', category: 'Settings' }
      ]);
    } catch (error) {
      // Use default permissions if API fails
      setPermissions([
        { id: 1, name: 'view_files', display_name: 'View Files', category: 'Files' },
        { id: 2, name: 'upload_files', display_name: 'Upload Files', category: 'Files' },
        { id: 3, name: 'delete_files', display_name: 'Delete Files', category: 'Files' },
        { id: 4, name: 'share_files', display_name: 'Share Files', category: 'Files' },
        { id: 5, name: 'manage_users', display_name: 'Manage Users', category: 'Users' },
        { id: 6, name: 'manage_settings', display_name: 'Manage Settings', category: 'Settings' }
      ]);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Role name is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      const roleData = {
        name: formData.name,
        description: formData.description,
        permissions: formData.permissions
      };
      await roleService.dataroom.create(dataroomUuid, roleData);
      toast.success('Role created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create role');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      const roleData = {
        name: formData.name,
        description: formData.description,
        permissions: formData.permissions
      };
      await roleService.dataroom.update(dataroomUuid, selectedRole.id, roleData);
      toast.success('Role updated successfully');
      setShowEditModal(false);
      resetForm();
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update role');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsSubmitting(true);
      await roleService.dataroom.delete(dataroomUuid, selectedRole.id);
      toast.success('Role deleted successfully');
      setShowDeleteModal(false);
      setSelectedRole(null);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete role');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Removed handleManagePermissions and handleSavePermissions as Manage Permissions action is removed

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      permissions: []
    });
    setFormErrors({});
    setSelectedRole(null);
  };

  const columns = [
    {
      key: 'name',
      header: 'Role Name',
      sortable: true,
      render: (value, row) => row ? (
        <div className="flex items-center">
          <Shield className="w-4 h-4 text-gray-400 mr-2" />
          <div>
            <p className="font-medium text-gray-900">{row.display_name || row.name || 'Unnamed'}</p>
            <p className="text-xs text-gray-500">{row.name || 'N/A'}</p>
          </div>
        </div>
      ) : <span className="text-gray-400">N/A</span>
    },
    {
      key: 'description',
      header: 'Description',
      sortable: false,
      render: (value, row) => row ? (
        <span className="text-sm text-gray-600">
          {row.description || 'No description'}
        </span>
      ) : <span className="text-gray-400">N/A</span>
    },
    {
      key: 'permissions',
      header: 'Permissions',
      sortable: false,
      render: (value, row) => {
        if (!row || !row.permissions || row.permissions.length === 0) {
          return <span className="text-sm text-gray-400">No permissions</span>;
        }
        
        const permissionNames = row.permissions.map(p => {
          if (typeof p === 'object') {
            return p.display_name || p.name || 'Unknown';
          }
          return p;
        });
        
        // Show first 3 permissions and count if more
        const displayCount = 3;
        const visiblePermissions = permissionNames.slice(0, displayCount);
        const remainingCount = permissionNames.length - displayCount;
        const allPermissions = permissionNames;
        
        return (
          <div className="flex flex-wrap gap-1 relative">
            {visiblePermissions.map((permission, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-700"
              >
                {permission}
              </span>
            ))}
            {remainingCount > 0 && (
              <div className="relative inline-block">
                <span 
                  className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700 cursor-pointer hover:bg-blue-200 transition-colors"
                  onMouseEnter={() => setHoveredRoleId(row.id)}
                  onMouseLeave={() => setHoveredRoleId(null)}
                >
                  +{remainingCount} more
                </span>
                
                {/* Popover */}
                {hoveredRoleId === row.id && (
                  <div 
                    className="absolute z-50 bottom-full left-0 mb-2 p-3 bg-white rounded-lg shadow-xl border border-gray-200 min-w-[200px] max-w-[300px]"
                    onMouseEnter={() => setHoveredRoleId(row.id)}
                    onMouseLeave={() => setHoveredRoleId(null)}
                  >
                    <div className="text-xs font-semibold text-gray-700 mb-2 border-b pb-1">
                      All Permissions ({allPermissions.length})
                    </div>
                    <div className="flex flex-wrap gap-1 max-h-64 overflow-y-auto">
                      {allPermissions.map((permission, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-700"
                        >
                          {permission}
                        </span>
                      ))}
                    </div>
                    {/* Arrow pointing down */}
                    <div className="absolute -bottom-2 left-4 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-white"></div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (value, row) => {
        if (!row) return <span className="text-gray-400">N/A</span>;
        
        // Check for is_active_text first, then fall back to is_active
        const statusText = row.is_active_text || (row.is_active === 1 || row.is_active === true ? 'Active' : 'Inactive');
        const isActive = statusText?.toLowerCase() === 'active' || row.is_active === 1 || row.is_active === true;
        
        return (
          <span className={`px-3 py-1 text-xs font-medium rounded-full ${
            isActive
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {statusText}
          </span>
        );
      }
    }
  ];

  const actions = (row) => row ? [
    {
      icon: Edit,
      label: 'Edit',
      onClick: () => {
        setSelectedRole(row);
        setFormData({
          name: row.name,
          description: row.description || '',
          permissions: row.permissions ? row.permissions.map(p => p.id || p) : []
        });
        setShowEditModal(true);
      },
      disabled: row.is_system
    },
    {
      icon: Trash2,
      label: 'Delete',
      onClick: () => {
        setSelectedRole(row);
        setShowDeleteModal(true);
      },
      className: 'text-red-600 hover:text-red-800',
      disabled: row.is_system
    }
  ] : [];

  // Group permissions by category - ensure permissions is an array
  const groupedPermissions = Array.isArray(permissions) 
    ? permissions.reduce((acc, permission) => {
        const category = permission.category || 'General';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(permission);
        return acc;
      }, {})
    : {};

  if (!dataroomUuid) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
            <p className="text-yellow-800">
              Please select a dataroom from the Datarooms page to manage roles.
            </p>
          </div>
          <button
            onClick={() => navigate('/datarooms')}
            className="mt-3 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Go to Datarooms
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center text-white mr-4">
                  <Shield className="w-6 h-6" />
                </div>
                Dataroom Roles Management
              </h1>
              <p className="text-gray-600 mt-2 ml-16">
                Manage roles and permissions for dataroom: {dataroomUuid}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  fetchRoles();
                  fetchPermissions();
                  toast.success('Data refreshed successfully');
                }}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <CreateButton
                onClick={() => setShowCreateModal(true)}
              >
                Create New Role
              </CreateButton>
            </div>
          </div>
        </div>

        {/* Statistics Cards - Removed as per requirement */}

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center mb-4">
            <Search className="w-5 h-5 text-gray-400 mr-2" />
            <h3 className="font-medium text-gray-900">Search & Filter</h3>
          </div>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search roles by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>
        </div>

        {/* Roles Table */}
        {loading ? (
          <PageLoader />
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <EnhancedDataTable
              data={roles}
              columns={columns}
              actions={actions}
              emptyMessage="No roles found for this dataroom"
              emptyIcon={Shield}
              showColumnControls={true}
              enableColumnResize={true}
              enableDragDrop={true}
              pagination={pagination}
              onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
              itemName="roles"
              onSort={(key, direction) => {
                const sorted = [...roles].sort((a, b) => {
                  let aVal = a[key];
                  let bVal = b[key];
                  
                  if (key === 'name') {
                    aVal = a.display_name || a.name || '';
                    bVal = b.display_name || b.name || '';
                  }
                  
                  if (aVal < bVal) return direction === 'asc' ? -1 : 1;
                  if (aVal > bVal) return direction === 'asc' ? 1 : -1;
                  return 0;
                });
                setRoles(sorted);
              }}
              showRefresh={true}
              onRefresh={() => {
                fetchRoles();
                fetchPermissions();
              }}
            />
          </div>
        )}

        {/* Create Role Modal */}
        <EnhancedModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          title="Create New Role"
          size="large"
          closeOnEscape={true}
          closeOnOutsideClick={true}
          showCloseButton={true}
        >
          <ResponsiveForm onSubmit={handleCreate}>
            <ResponsiveInput
              label="Role Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={formErrors.name}
              placeholder="e.g., viewer, editor, admin"
              required
            />
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                placeholder="Describe the role's purpose..."
              />
            </div>

            {/* Permissions Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Permissions
              </label>
              <div className="space-y-4 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-4">
                {Object.entries(groupedPermissions).map(([category, categoryPermissions]) => (
                  <div key={category} className="space-y-2">
                    <h4 className="font-medium text-gray-900 text-sm">{category}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {categoryPermissions.map(permission => (
                        <label
                          key={permission.id}
                          className="flex items-center p-2 rounded hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(permission.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  permissions: [...formData.permissions, permission.id]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  permissions: formData.permissions.filter(id => id !== permission.id)
                                });
                              }
                            }}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <div className="ml-3">
                            <p className="text-sm text-gray-900">
                              {permission.display_name || permission.name}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
              <CancelButton
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
              />
              <SaveButton
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Role'}
              </SaveButton>
            </div>
        </ResponsiveForm>
        </EnhancedModal>

        {/* Edit Role Modal */}
        <EnhancedModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            resetForm();
          }}
          title="Edit Role"
          size="large"
          closeOnEscape={true}
          closeOnOutsideClick={true}
          showCloseButton={true}
        >
        <ResponsiveForm onSubmit={handleEdit}>
          <ResponsiveInput
            label="Role Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={formErrors.name}
            placeholder="e.g., viewer, editor, admin"
            required
          />
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              placeholder="Describe the role's purpose..."
            />
          </div>

          {/* Permissions Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Permissions
            </label>
            <div className="space-y-4 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-4">
              {Object.entries(groupedPermissions).map(([category, categoryPermissions]) => (
                <div key={category} className="space-y-2">
                  <h4 className="font-medium text-gray-900 text-sm">{category}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {categoryPermissions.map(permission => (
                      <label
                        key={permission.id}
                        className="flex items-center p-2 rounded hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.permissions.includes(permission.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                permissions: [...formData.permissions, permission.id]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                permissions: formData.permissions.filter(id => id !== permission.id)
                              });
                            }
                          }}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <div className="ml-3">
                          <p className="text-sm text-gray-900">
                            {permission.display_name || permission.name}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
            <CancelButton
              type="button"
              onClick={() => {
                setShowEditModal(false);
                resetForm();
              }}
            />
            <UpdateButton
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : 'Update Role'}
            </UpdateButton>
          </div>
        </ResponsiveForm>
      </EnhancedModal>

      {/* Manage Permissions Modal - Removed as Manage Permissions action is removed */}

      {/* Delete Confirmation Modal */}
      <EnhancedModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedRole(null);
        }}
        title="Delete Role"
        size="medium"
        closeOnEscape={true}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete the role "{selectedRole?.display_name}"? 
          This action cannot be undone and will affect all users with this role.
        </p>
        <div className="flex justify-end gap-3 pt-4">
          <CancelButton
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedRole(null);
            }}
          />
          <DeleteButton
            onClick={handleDelete}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Deleting...' : 'Delete Role'}
          </DeleteButton>
        </div>
      </EnhancedModal>
      </div>
    </div>
  );
};

export default DataroomRoles;